// Workbook persistence. The workbook lives in a PROJECT ATTACHMENT, the way
// paper stores its document, with IndexedDB as the hot local tier.
//
//   0. IndexedDB          — this browser. Instant warm start, no rpc cost.
//   1. Project attachment — the shared copy. JSON only; no size cliff.
//
// `projectApp.public` is read on hydrate so anything saved there by an earlier
// build still opens, but it is no longer written: it silently rejects past
// ~20,000 chars, and a real model crosses that (a 200-row appraisal is ~22KB).
// A store with a cliff you discover by losing work is the wrong primary.
//
// Cold start paints the LOCAL copy immediately and reconciles with the shared
// one when it arrives, comparing `savedAt`. Local newer means unsynced work, so
// it is kept and pushed; remote newer means a teammate saved, so it replaces.
//
// NOTHING is written to either tier until that reconciliation has concluded —
// see `restored`. The app is dirty within milliseconds of mount and the read is
// seconds behind it, so an open gate meant the DEFAULT workbook was saved over
// the project's on any browser with no local copy to paint first.
//
// Cadence (sdk-pitfalls.md §1.5): the bridge stops at 499 invocations/minute
// and an app can spend that on itself — one debounce feeding straight into an
// upload is one upload per settle, and a drag is hundreds of settles. So:
// debounce to IDB at 500ms, FLOOR the remote write at 60s driven by both a
// settle timer and a periodic interval, single-flight it, and `force` only on
// the way out. The saved-hash advances only on a confirmed OK, and a refusal
// backs off in time rather than giving up — a passing outage must not become
// "not saved" until the user reloads.

import type { Workbook } from '@/model/types';
import { createWorkbook } from '@/model/workbook';
import {
  ATTACHMENT_NAME,
  PUBLIC_CAP,
  deserialize,
  hash,
  resolveAppId,
  savedAtOf,
  serialize,
} from '@/model/persistFormat';
import { idbGet, idbSet } from './idb';
import { readJsonAttachment, uploadJsonAttachment } from './attachmentStore';
import { readAppData, writeAppData } from './appDataStore';
import { readState } from './adapter';

export { ATTACHMENT_NAME, PUBLIC_CAP, bareAppId, deserialize, resolveAppId, serialize } from '@/model/persistFormat';

export type SaveTarget = 'projectApp' | 'attachment' | 'local';
export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'retrying' | 'error';

export interface SaveStatus {
  state: SaveState;
  target: SaveTarget;
  /** Human-readable detail — shown verbatim, so it must say something true. */
  detail: string;
  lastSavedAt: number | null;
  failures: number;
}

const IDB_DEBOUNCE_MS = 500;
const READ_RETRY_MS = 5_000;
const REMOTE_SETTLE_MS = 3_000;
const REMOTE_FLOOR_MS = 60_000;
const BACKOFF_CAP_MS = 15 * 60_000;

export function projectKey(): string {
  const p = readState<Record<string, unknown>>('project');
  const props = (p?.['properties'] ?? {}) as Record<string, unknown>;
  const id = p?.['id'] ?? p?.['projectId'] ?? props['id'] ?? props['project_id'];
  return id == null ? 'noproject' : String(id);
}


// ---------------------------------------------------------------------------
// The controller
// ---------------------------------------------------------------------------

export class PersistenceController {
  private getWorkbook: () => Workbook;
  private onStatus: (status: SaveStatus) => void;

  private key = 'noproject';

  private idbTimer: ReturnType<typeof setTimeout> | null = null;
  private readRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private readFailures = 0;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;

  /** Advances ONLY on a confirmed OK — see the rollback trap below. */
  private savedHash = '';
  private inFlight: Promise<void> | null = null;
  private earliestNextUpload = 0;
  private failures = 0;
  private target: SaveTarget = 'local';
  private lastSavedAt: number | null = null;

  /**
   * Latched only when hydration reached a CONCLUSION about the project's copy.
   * A run that gave up early — because the bridge had not yet said which
   * project this is — leaves it false so the retries come back.
   */
  private hydrateDone = false;
  /** Joins overlapping hydrate calls; the read they retry takes seconds. */
  private hydrating: Promise<void> | null = null;
  /**
   * THE WRITE GATE. Nothing goes to either tier until the restore has landed.
   *
   * This is the one that was losing work. The default workbook emits within
   * milliseconds of mount — the Giraffe data arriving rewrites the system
   * sheets — while the read that would say "this project already HAS a
   * workbook" takes seconds. With the gate open, the 500ms debounce wrote the
   * empty document to IndexedDB and the 3s settle timer uploaded it to the
   * project attachment, both BEFORE the read returned. On a browser with no
   * local copy to paint, that read then came back with the empty workbook this
   * session had just written over it, and reported "Loaded from the project."
   *
   * (`~/Documents/code/paper` calls this `restoreComplete` and sets it only
   * after a snapshot has been applied. Same rule, same reason.)
   */
  private restored = false;

  constructor(getWorkbook: () => Workbook, onStatus: (s: SaveStatus) => void) {
    this.getWorkbook = getWorkbook;
    this.onStatus = onStatus;
  }

  start(): void {
    // The periodic tick matters as much as the settle timer: without it, an
    // edit made 10s after an upload sits unsent until the next unrelated pause.
    this.interval = setInterval(() => void this.maybeUpload(false), REMOTE_FLOOR_MS);
  }

  stop(): void {
    if (this.idbTimer) clearTimeout(this.idbTimer);
    if (this.settleTimer) clearTimeout(this.settleTimer);
    if (this.readRetryTimer) clearTimeout(this.readRetryTimer);
    if (this.interval) clearInterval(this.interval);
    this.idbTimer = null;
    this.settleTimer = null;
    this.readRetryTimer = null;
    this.interval = null;
  }

  /** Called when the SDK tells us which app and project we are. */
  bind(selectedProjectApp: unknown, appsByAppID: unknown): void {
    // Resolved here only to fail early and visibly when the app id cannot be
    // worked out — `appDataStore` resolves it again at write time, from live
    // state rather than from whatever was bound seconds ago.
    if (resolveAppId(selectedProjectApp, appsByAppID) === null) {
      console.warn('[gtables] no app id yet — appData cannot be written until there is one');
    }
    // Keyed by PROJECT, not by app instance: the workbook belongs to the
    // project, and the app id can change when someone re-adds the app.
    const key = `gtables:${projectKey()}`;
    // A re-key BEFORE the restore landed means the last attempt read the wrong
    // drawer, so let it run again on the right one. After the restore, a
    // re-hydrate would drop work on the floor, so the conclusion stands.
    if (key !== this.key && !this.restored) this.hydrateDone = false;
    this.key = key;
    this.target = 'attachment';
  }

  storageKey(): string {
    return this.key;
  }

  /**
   * Hydrate once, in two beats.
   *
   * Beat one is the local copy — synchronous enough to paint, and it is what
   * makes a cold start feel instant. Beat two is the shared attachment, which
   * needs `fetchProjectDetails` plus a signed URL and can take a second or two.
   * `apply` is therefore called up to twice, and the second call only happens
   * when the shared copy is genuinely newer than what is already on screen.
   */
  /** Kept so a later retry can apply what it reads, as hydrate would have. */
  private apply: ((wb: Workbook, from: SaveTarget) => void) | null = null;

  async hydrate(apply: (wb: Workbook, from: SaveTarget) => void): Promise<void> {
    this.apply = apply;
    if (this.hydrateDone) return;
    // The three retry timers and the state listener all call this, and the read
    // they are retrying takes seconds — so an overlapping call joins the one in
    // flight instead of starting a second one.
    if (this.hydrating) return this.hydrating;
    this.hydrating = this.runHydrate(apply).finally(() => {
      this.hydrating = null;
    });
    return this.hydrating;
  }

  private async runHydrate(apply: (wb: Workbook, from: SaveTarget) => void): Promise<void> {
    /*
     * No project id yet, so there is nothing to conclude.
     *
     * The bridge answers before the handshake completes, so the first bind can
     * only key IndexedDB as `gtables:noproject`. Concluding on that key is the
     * catastrophic path: it opens the write gate with the DEFAULT workbook on
     * screen and no idea whose attachment it is about to overwrite. Returning
     * WITHOUT latching is what makes the retry timers mean something — they
     * were previously all no-ops, because the latch was taken on entry.
     */
    if (this.key === 'gtables:noproject' || this.key === 'noproject') {
      // The KEY, not a fresh `projectKey()` read: the key is what the IndexedDB
      // read below and every later write actually use, and re-reading state
      // here could clear the guard while the key it guards is still stale.
      this.status('pending', 'Waiting for the project…');
      return;
    }

    let localAt = 0;
    const local = await idbGet<unknown>(this.key);
    if (local) {
      const wb = deserialize(local);
      if (wb) {
        localAt = savedAtOf(local);
        apply(wb, 'local');
        this.status('pending', 'Opened this browser’s copy. Checking the project…');
      }
    }

    const remote = await this.readShared();
    if (!remote) {
      if (this.readBlocked) {
        // Not "nothing saved" — this project HAS a workbook and this session
        // could not read it. Saying the former, and then saving over it, is how
        // a model disappears. The gate opens so the local tier can keep the
        // user's work, as the message promises; `readBlocked` holds the upload.
        this.conclude();
        this.reportBlocked();
        return;
      }
      this.conclude();
      if (localAt === 0) this.status('idle', 'Nothing saved in this project yet.');
      else this.status('pending', 'No project copy yet — this will be the first save.');
      return;
    }

    const wb = remote.wb;
    const remoteAt = savedAtOf(remote.raw);

    if (remoteAt >= localAt) {
      apply(wb, remote.target);
      this.target = remote.target;
      this.savedHash = hash(JSON.stringify(serialize(wb, 0)));
      this.lastSavedAt = remoteAt || null;
      this.conclude();
      this.status('saved', remote.target === 'attachment' ? 'Loaded from the project.' : 'Loaded from app settings.');
      return;
    }

    // The local copy is newer: work that never reached the project. Keep it on
    // screen and get it uploaded rather than silently reverting to the project.
    this.conclude();
    this.status('pending', 'This browser has newer changes than the project — saving them now.');
    void this.flush(true);
  }

  /**
   * The project HAS a saved workbook that could not be read.
   *
   * While this is true nothing is uploaded. A read blocked by CORS, an expired
   * url or a corrupt blob looks exactly like an empty project to a caller with
   * only null to work with — and the next autosave would then write this
   * session's workbook straight over someone else's model. Refusing to save is
   * the safe failure; saving over unread work is not.
   */
  private readBlocked: string | null = null;

  private readonly UNPARSEABLE = 'The project’s saved workbook is not in a format this build can read.';

  /**
   * The shared copy — appData FIRST, because it needs no network request.
   *
   * `public` arrives in the broadcast state with the handshake, so reading it
   * cannot fail the way the attachment does: the attachment's bytes are in GCS
   * behind a signed url whose bucket sends no `Access-Control-Allow-Origin` for
   * this app's origin, and the browser therefore discards a 200 it received
   * intact. See giraffe/appDataStore.ts.
   *
   * Both tiers are still read, and the NEWER `savedAt` wins — that is what
   * carries a project whose copy is currently in an attachment across to
   * appData without a migration step, and what stops a stale tier winning if
   * someone saves from an older build.
   */
  private async readShared(): Promise<{ raw: unknown; wb: Workbook; target: SaveTarget } | null> {
    type Candidate = { raw: unknown; wb: Workbook; target: SaveTarget };
    const found: Candidate[] = [];
    /*
     * Computed FRESH on every call, and assigned only once the read has
     * answered.
     *
     * Fresh, because a run that leaves the previous verdict in place cannot
     * clear one: the attachment coming back `missing` — a definitive answer, not
     * a failure — used to leave the session blocked forever on a detail from
     * minutes ago. Assigned late, because clearing the flags before the await
     * left them null for the width of the read, and a settle timer firing in
     * that window uploaded happily.
     */
    let blocked: string | null = null;
    let attachmentBlocked: string | null = null;

    const appData = readAppData();
    if (appData) {
      const wb = deserialize(appData);
      if (wb) found.push({ raw: appData, wb, target: 'projectApp' });
      else blocked = this.UNPARSEABLE;
    }

    const attachment = await readJsonAttachment<unknown>(ATTACHMENT_NAME);
    if (attachment.kind === 'ok') {
      const wb = deserialize(attachment.value);
      if (wb) found.push({ raw: attachment.value, wb, target: 'attachment' });
      else {
        /*
         * A blob that will not deserialise is UNREADABLE, not missing — the same
         * distinction `readJsonAttachment` draws for corrupt JSON, and it has to
         * be drawn HERE rather than at the call site. When it was only checked
         * in `hydrate`, the scheduled read retry went straight past it and saved
         * over a document we could not parse.
         */
        blocked = this.UNPARSEABLE;
        attachmentBlocked = this.UNPARSEABLE;
      }
    } else if (attachment.kind === 'unreadable') {
      blocked = attachment.detail;
      attachmentBlocked = attachment.detail;
    }
    // `missing` is an ANSWER: there is nothing there, and nothing is blocked.

    // A tier we CAN read is the shared copy, so we are not blind — whatever the
    // other tier did. `attachmentBlocked` is the narrower guard that survives,
    // because it is the only one a write can destroy.
    if (found.length > 0) blocked = null;

    this.readBlocked = blocked;
    this.attachmentBlocked = attachmentBlocked;

    if (found.length === 0) return null;
    found.sort((a, b) => savedAtOf(b.raw) - savedAtOf(a.raw));
    return found[0]!;
  }

  /**
   * The attachment specifically could not be read.
   *
   * Narrower than `readBlocked`, and it is the one that matters for safety: the
   * only write that can destroy an unread copy is a write to the tier holding
   * it. Writing appData while the attachment is unreadable overwrites nothing —
   * a different slot, and the attachment's bytes stay exactly as they are.
   */
  private attachmentBlocked: string | null = null;

  isReadBlocked(): boolean {
    return this.readBlocked !== null;
  }

  /**
   * A blocked read must not be a wall.
   *
   * `fetchProjectDetails` goes through the bridge, and the read most likely to
   * miss is the FIRST one after a cold start — the handshake may not be warm
   * yet. Refusing to save while the project's copy is unread is right, but
   * leaving that refusal standing until someone notices a button says "Retry"
   * means the project gets no saves at all for the rest of the session. So the
   * read retries itself, further apart each time, and the status says when.
   *
   * Returns the wait, so the message can name it.
   */
  private scheduleReadRetry(): number {
    if (this.readRetryTimer) clearTimeout(this.readRetryTimer);
    const wait = Math.min(READ_RETRY_MS * 2 ** this.readFailures, BACKOFF_CAP_MS);
    this.readFailures += 1;
    this.readRetryTimer = setTimeout(() => {
      this.readRetryTimer = null;
      void this.retryRead();
    }, wait);
    return wait;
  }


  /**
   * The one place that reports a blocked read.
   *
   * An unreadable copy is only an ERROR if it stops us saving, and since appData
   * is a different slot from the attachment, it usually does not. That
   * distinction is the difference between a red "Retry" sitting there on a
   * freshly loaded sheet nobody has touched — which is what this looked like
   * before, while writes were landing perfectly well — and an ordinary "Save"
   * that the autosave clears within three seconds.
   *
   * Retries are scheduled ONLY in the stuck case. Retrying a CORS refusal is
   * pointless — the bucket's policy will not change between attempts — and all
   * it produced was a console full of red.
   */
  private reportBlocked(): void {
    console.warn('[gtables] the project copy could not be read:', this.readBlocked);
    if (this.canWriteAppData()) {
      this.status(
        'pending',
        `${this.readBlocked} Saving to app settings instead, which this app can always read back.`
      );
      return;
    }
    const wait = this.scheduleReadRetry();
    this.status(
      'error',
      `${this.readBlocked} Not saving over it — trying again in ${Math.round(wait / 1000)}s. Your work is kept in this browser.`
    );
  }

  /** Whether the workbook still fits the tier that needs no attachment write. */
  private canWriteAppData(): boolean {
    return JSON.stringify(serialize(this.getWorkbook())).length <= PUBLIC_CAP;
  }

  /**
   * Try the shared copy again. What Save means while a read is blocked.
   *
   * If it comes back, the conflict is the same one hydrate settles: work done
   * in this session while the project could not be read is the newer of the
   * two and is kept, and a session that has changed nothing takes the project's
   * copy instead. Either way the block lifts and saving resumes.
   */
  async retryRead(): Promise<boolean> {
    if (this.readRetryTimer) clearTimeout(this.readRetryTimer);
    this.readRetryTimer = null;
    this.status('pending', 'Trying to read the project copy…');
    /*
     * The flags are NOT cleared before the read.
     *
     * They used to be, and that left `readBlocked` null for the width of an
     * await — a settle timer firing inside that window uploaded happily. It is
     * how a session whose attachment was permanently unreadable still managed a
     * successful PUT and then reported "Retry" straight after. `readShared`
     * clears them itself, once it has actually read something.
     */
    const shared = await this.readShared();
    if (this.readBlocked) {
      this.reportBlocked();
      return false;
    }
    this.readFailures = 0;

    const local = serialize(this.getWorkbook(), 0);
    const dirty = hash(JSON.stringify(local)) !== this.savedHash;
    if (shared && !dirty) {
      this.apply?.(shared.wb, shared.target);
      this.savedHash = hash(JSON.stringify(serialize(shared.wb, 0)));
      this.lastSavedAt = savedAtOf(shared.raw) || null;
      this.status('saved', 'Read the project copy.');
      return true;
    }
    // Either there is nothing there, or this session has work the project has
    // not seen. Both end the same way: send what is on screen.
    await this.flush(true);
    return true;
  }

  /**
   * Hydration reached a conclusion: stop retrying, and open the write gate.
   * Every terminal branch of `runHydrate` calls this, and nothing else does.
   */
  private conclude(): void {
    this.hydrateDone = true;
    this.restored = true;
  }

  isRestored(): boolean {
    return this.restored;
  }

  /** What Save means before the restore has landed: try the read again. */
  async retryHydrate(): Promise<void> {
    if (this.restored || !this.apply) return;
    await this.hydrate(this.apply);
  }

  /**
   * Every change lands here. Cheap: it only arms timers.
   *
   * `userEdit` is false when the PROJECT caused the change — Giraffe
   * broadcasting a usage list rewrites the system sheets, which does alter the
   * saved document and is worth sending. It is not, however, the user's unsaved
   * work, and saying it is puts a red dot on a sheet nobody has touched within a
   * second of it opening. So it still saves; it just does not claim to be
   * theirs.
   */
  markDirty(userEdit = true): void {
    if (!this.restored) return; // see the write gate: defaults must not clobber the saved copy
    if (this.idbTimer) clearTimeout(this.idbTimer);
    this.idbTimer = setTimeout(() => void this.saveLocal(), IDB_DEBOUNCE_MS);
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => void this.maybeUpload(false), REMOTE_SETTLE_MS);
    if (userEdit) this.status('pending', 'Unsaved changes.');
  }

  private async saveLocal(): Promise<void> {
    await idbSet(this.key, serialize(this.getWorkbook()));
  }

  /**
   * `force` is for the callers with no next attempt to defer to: pagehide,
   * unmount, the closing signal, and a manual Save. Everything else respects
   * the floor.
   */
  async flush(force = false): Promise<void> {
    // pagehide and unmount reach here too, and a teardown mid-hydration must
    // not be the thing that writes the default workbook to the hot tier.
    if (!this.restored) return;
    await this.saveLocal();
    await this.maybeUpload(force);
  }

  private async maybeUpload(force: boolean): Promise<void> {
    if (!this.restored) return;
    // Hash the CONTENT only (savedAt zeroed) — hashing the timestamp would make
    // every serialisation look like a change and the dedupe would never fire.
    const h = hash(JSON.stringify(serialize(this.getWorkbook(), 0)));
    if (h === this.savedHash) {
      /*
       * Nothing to send — and SAYING so is the point.
       *
       * The dirty flag is armed by anything the user does, because a change
       * missed is work lost and a change over-reported is only noise. But that
       * makes "pending" a claim about intent, not about content: click a cell,
       * open an editor, type and undo, and the button lights up over a document
       * identical to the saved one. Returning silently left it lit forever —
       * Save pressed, nothing sent, still saying Save, which reads exactly like
       * a save that failed.
       *
       * The hash is the only thing that knows the truth, so this is where the
       * claim gets withdrawn.
       */
      if (this.lastSavedAt !== null) this.status('saved', 'Already saved — nothing had changed.');
      return;
    }
    const text = JSON.stringify(serialize(this.getWorkbook()));

    // NOTE: no blanket refusal here any more. `uploadShared` decides, per tier,
    // because only a write to the tier we could not read can destroy anything.

    const now = Date.now();
    if (!force && now < this.earliestNextUpload) return;

    // Single-flight. Because the saved hash only advances on a confirmed OK (as
    // it must), every caller inside the width of one rpc still sees the old
    // hash and would otherwise start its own identical upload.
    if (this.inFlight) {
      await this.inFlight;
      return;
    }

    this.inFlight = this.upload(text, h).finally(() => {
      this.inFlight = null;
    });
    await this.inFlight;
  }

  private async upload(text: string, h: string): Promise<void> {
    this.status('saving', 'Saving to the project…');
    const ok = await this.uploadShared(text);

    if (ok.ok) {
      // The rollback trap: setting the saved hash BEFORE the async resolved once
      // recorded a REJECTED write as saved, and the state vanished on reload.
      // It advances here and nowhere else.
      this.savedHash = h;
      this.failures = 0;
      this.lastSavedAt = Date.now();
      this.earliestNextUpload = this.lastSavedAt + REMOTE_FLOOR_MS;
      this.status('saved', ok.detail);
      return;
    }

    this.failures += 1;
    // Back off in TIME, never stop. A failure counter that gives up turns a
    // passing rate limit into "not saved" until the user reloads.
    const wait = Math.min(REMOTE_FLOOR_MS * 2 ** this.failures, BACKOFF_CAP_MS);
    this.earliestNextUpload = Date.now() + wait;
    this.status(
      'retrying',
      `${ok.detail} Still trying, further apart each time (next in ${Math.round(wait / 1000)}s). Your work is saved in this browser.`
    );
  }

  /**
   * Write the shared copy to the tier that can hold it.
   *
   * appData when it fits, because it is the tier that can be READ back from
   * this origin. The attachment only for a workbook over the cap — `public`
   * rejects rather than truncates, so the cap is checked before the call and
   * a `too-large` answer is still honoured as a fallback rather than trusted
   * to be impossible.
   */
  private async uploadShared(text: string): Promise<{ ok: boolean; detail: string }> {
    if (text.length <= PUBLIC_CAP) {
      const res = await writeAppData(JSON.parse(text));
      if (res.ok) {
        this.target = 'projectApp';
        return { ok: true, detail: 'Saved to the project.' };
      }
      if (res.reason !== 'too-large') {
        return { ok: false, detail: `Could not save to the project (${res.reason}).` };
      }
      // Bigger than `public` will take after all — fall through to the
      // attachment, which is exactly what it is for.
    }

    /*
     * The attachment holds the copy we may have failed to read, so THIS is the
     * write that has to refuse. appData above does not: it is a different slot,
     * and writing it leaves the attachment's bytes untouched.
     */
    if (this.attachmentBlocked) {
      return {
        ok: false,
        detail: `${this.attachmentBlocked} This workbook is too large for app settings, so it cannot be saved without overwriting it.`,
      };
    }
    const res = await uploadJsonAttachment(ATTACHMENT_NAME, JSON.parse(text));
    if (res) {
      this.target = 'attachment';
      return { ok: true, detail: 'Saved to the project.' };
    }
    return { ok: false, detail: 'Could not upload the workbook to the project.' };
  }

  private status(state: SaveState, detail: string): void {
    this.onStatus({
      state,
      target: this.target,
      detail,
      lastSavedAt: this.lastSavedAt,
      failures: this.failures,
    });
  }

  /** For the "New workbook" action. */
  reset(): Workbook {
    this.savedHash = '';
    return createWorkbook();
  }
}
